import type { Node as PMNode, Mark as PMMark } from '@atlaskit/editor-prosemirror/model';
import type { BlockContent, PhrasingContent } from 'mdast';
import type { FromPMHandlers } from './types';

/**
 * Wrap an mdast phrasing content node with marks, building up nested nodes
 * for strong, emphasis, code, link, and strikethrough marks.
 */
export function applyMarks(node: PhrasingContent, marks: readonly PMMark[]): PhrasingContent {
	let result: PhrasingContent = node;
	for (const mark of marks) {
		switch (mark.type.name) {
			case 'strong':
				result = { type: 'strong', children: [result] };
				break;
			case 'em':
				result = { type: 'emphasis', children: [result] };
				break;
			case 'code':
				// Inline code — unwrap text value
				if (result.type === 'text') {
					result = { type: 'inlineCode', value: result.value };
				}
				break;
			case 'link':
				result = {
					type: 'link',
					url: mark.attrs.href ?? '',
					title: mark.attrs.title ?? null,
					children: [result],
				};
				break;
			case 'strike':
			case 'strikethrough':
				// GFM strikethrough ~~text~~ — symmetric with the `delete` handler in to-pm
				result = { type: 'delete', children: [result] };
				break;
			default:
				// Unknown mark — skip
				break;
		}
	}
	return result;
}

/**
 * Convert block-level PM children to mdast BlockContent using the given handlers.
 */
export function convertBlockChildren(node: PMNode, handlers: FromPMHandlers): BlockContent[] {
	const children: BlockContent[] = [];
	node.forEach((child) => {
		const handler = handlers[child.type.name];
		if (handler) {
			const result = handler(child);
			if (result) {
				if (Array.isArray(result)) {
					children.push(...(result as BlockContent[]));
				} else {
					children.push(result as BlockContent);
				}
			}
		}
	});
	return children;
}

/** Convert inline PM children to mdast PhrasingContent nodes using applyMarks for each inline child. */
export function convertInlineChildren(node: PMNode, handlers: FromPMHandlers): PhrasingContent[] {
	const children: PhrasingContent[] = [];
	node.forEach((child) => {
		if (child.isText) {
			const textNode = applyMarks({ type: 'text', value: child.text ?? '' }, child.marks);
			children.push(textNode);
		} else {
			const handler = handlers[child.type.name];
			if (handler) {
				const result = handler(child);
				if (result) {
					const nodes = Array.isArray(result) ? result : [result];
					children.push(...(nodes as PhrasingContent[]));
				}
			}
		}
	});
	return children;
}
